import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { logError } from '../../../../utils/logger';
import {
  ControlledSelect,
  ControlledTextField,
  ModalWrapper,
  SectionTitle,
} from '../../../shared/components';
import { AADHAAR_REGEX, PAN_REGEX } from '../../../shared/constants';
import {
  AADHAAR_LENGTH,
  AADHAAR_MESSAGE,
  GENDER_OPTIONS,
  NAME_MAX_LENGTH,
  PHONE_LENGTH,
} from '../employees.constants';
import { getDepartmentOptions, updateEmployeeDetails } from '../employees.service';
import { toEmployeeFormValues } from '../employees.utils';

/**
 * Row-oriented fields so the form lines its labels up the same way the read-only
 * EmployeeDetailsCard does - the edit screen should read as the card with inputs in it.
 *
 * The inputs stretch to whatever width the column gives them instead of sitting at a fixed
 * 280px, so the form fills the screen it is opened on rather than leaving a gutter down the
 * middle of a wide one and crowding its labels on a narrow one.
 *
 * The label column is a fixed width rather than `auto` so that every input starts and ends
 * at the same place down the form - stretched inputs behind labels of differing lengths
 * would otherwise line up on neither edge. `minWidth: 0` lets an input shrink below its
 * content width, which is what stops a row overflowing sideways.
 */
const FIELD_PROPS = {
  orientation: 'row',
  wrapperSx: {
    '& > div': {
      gridTemplateColumns: { xs: '9rem minmax(0, 1fr)', sm: '12rem minmax(0, 1fr)' },
      columnGap: 4,
      '& > :nth-child(2n)': {
        justifySelf: 'stretch',
        width: '100%',
        minWidth: 0,
      },
    },
  },
  sx: { width: '100%' },
};

/**
 * Two columns of fields on a normal screen, one on a narrow one, with the divider following
 * whichever way they are laid out. The global `grid--2-cols` helper is fixed at two, which
 * is what squeezed this form at smaller widths.
 */
const FIELD_COLUMNS_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  rowGap: 3,
  '& > * + *': {
    borderColor: 'divider',
    borderTop: { xs: '1px solid', md: 0 },
    borderLeft: { xs: 0, md: '1px solid' },
    pt: { xs: 3, md: 0 },
    pl: { xs: 0, md: 4 },
  },
};

/**
 * Employee edit screen, opened from the Edit button on the employee details view.
 *
 * Covers the fields the details card shows and the API can patch. Joining date, employment
 * status and documents are deliberately left out: status has its own endpoint and its own
 * control on the card, and the rest are set during hiring.
 */
interface EmployeeEditModalProps {
  open: boolean;
  employee?: Record<string, any>;
  onClose?: () => void;
  onSaved?: (employee: Record<string, any>) => void;
}

export default function EmployeeEditModal(props: EmployeeEditModalProps) {
  const { open, employee = {}, onClose, onSaved } = props;

  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);

  const methods = useForm({ defaultValues: toEmployeeFormValues(employee) });
  const { handleSubmit, reset } = methods;

  // Re-seed on open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    reset(toEmployeeFormValues(employee));
  }, [open, employee, reset]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    getDepartmentOptions()
      .then((options) => {
        if (active) setDepartments(options);
      })
      .catch((err) => {
        if (!active) return;
        toast.error(
          logError({
            message: 'Failed to fetch departments',
            error: err,
            code: 'DEPARTMENT_LIST_FETCH_FAILED',
          })
        );
        setDepartments([]);
      });

    return () => {
      active = false;
    };
  }, [open]);

  const closeIfIdle = () => {
    if (saving) return;
    onClose?.();
  };

  const saveEmployee = async (values) => {
    setSaving(true);
    try {
      const updated = await updateEmployeeDetails(employee.id, values);
      toast.success('Employee details updated');
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to update employee details',
          error: err,
          code: 'EMPLOYEE_UPDATE_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={closeIfIdle} isFullWidth>
      <FormProvider {...methods}>
        <form className='column gap-5' onSubmit={handleSubmit(saveEmployee)} noValidate>
          <SectionTitle
            title={`Edit ${employee.name || 'employee'} details`}
            meta={
              employee.employeeId ? (
                <span className='label--secondary'>{employee.employeeId}</span>
              ) : null
            }
          />

          <Box sx={FIELD_COLUMNS_SX}>
            <div className='column'>
              <ControlledTextField
                name='name'
                label='Employee name'
                placeholder='Enter employee name'
                rules={{
                  required: 'Employee name is required',
                  maxLength: {
                    value: NAME_MAX_LENGTH,
                    message: `Maximum ${NAME_MAX_LENGTH} characters allowed`,
                  },
                }}
                {...FIELD_PROPS}
              />

              <ControlledSelect
                name='gender'
                label='Gender'
                placeholder='Select gender'
                options={GENDER_OPTIONS}
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='contact'
                label='Contact number'
                type='tel'
                inputMode='numeric'
                placeholder='Enter contact number'
                rules={{
                  minLength: { value: PHONE_LENGTH, message: 'Contact number must be 10 digits' },
                  maxLength: { value: PHONE_LENGTH, message: 'Contact number must be 10 digits' },
                }}
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='email'
                label='Email ID'
                type='email'
                placeholder='Enter email ID'
                rules={{
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email ID' },
                }}
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='designation'
                label='Designation'
                placeholder='Enter designation'
                {...FIELD_PROPS}
              />

              <ControlledSelect
                name='departmentId'
                label='Department'
                placeholder='Select department'
                options={departments}
                {...FIELD_PROPS}
              />
            </div>

            <div className='column'>
              <ControlledTextField
                name='aadhaarNumber'
                label='Aadhaar number'
                inputMode='numeric'
                placeholder='Enter Aadhaar number'
                rules={{
                  maxLength: { value: AADHAAR_LENGTH, message: AADHAAR_MESSAGE },
                  pattern: { value: AADHAAR_REGEX, message: AADHAAR_MESSAGE },
                }}
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='panNumber'
                label='PAN number'
                placeholder='Enter PAN number'
                onBlurNormalize={(value) => (value ? value.toUpperCase() : value)}
                rules={{ pattern: { value: PAN_REGEX, message: 'Invalid PAN number' } }}
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='nationality'
                label='Nationality'
                placeholder='Enter nationality'
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='medicalCondition'
                label='Medical condition (if any)'
                placeholder='Enter medical condition'
                {...FIELD_PROPS}
              />

              <ControlledTextField
                name='currentSalary'
                label='Current salary'
                inputMode='numeric'
                placeholder='Enter current salary'
                prefix='₹'
                hideArrow
                rules={{
                  validate: (value) => {
                    if (!value) return true;
                    const amount = Number(String(value).replace(/[₹,]/g, '').trim());
                    if (Number.isNaN(amount)) return 'Current salary must be a number';
                    return amount >= 0 || 'Current salary cannot be negative';
                  },
                }}
                {...FIELD_PROPS}
              />
            </div>
          </Box>

          <div className='row row--end gap-3'>
            <button
              type='button'
              className='btn outlined-btn'
              onClick={closeIfIdle}
              disabled={saving}
            >
              Cancel
            </button>
            <button type='submit' className='btn cta-btn' disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
}
